import { LogoTechnique } from '@seddi/scene';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

import type { MockupLogo } from '../../types/mockupLogos';
import {
  setSelectedGraphicId,
  removeGraphic,
  addGraphic,
  replaceGraphicImage,
} from '../../store/appSlice';
import type { TRootState } from '../../store/appStateTypes';
import { getInitialTransformFromRegion } from './logos/logoPlacement';
import {
  calculateLogoSizeForRegion,
  calculateLogoSizeRetainingWidth,
  findBestAvailableRegionForNewLogo,
} from './logos/logoSizingStrategies';
import { MeasurementSystem } from '@seddi/core/lib/cjs';
import { CLOUD_CONVERT_API_KEY } from '../../constants/appConstants';
import { fileToImageUrl, needsCloudConvert } from '../../utils/logoConversion';
import { convertRegionName } from '../../utils/regionNameConverter';

const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'application/postscript',
  '.ai',
  '.eps',
  '.pdf',
  '.svg',
];

const ACCEPTED_LOGO_FILES: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],
  'application/pdf': ['.pdf'],
  'application/postscript': ['.eps', '.ai'],
};

const nameWithoutExtension = (filename: string) => filename.replace(/\.[^/.]+$/, '');

type UseGraphicsPanelProps = {
  disabled?: boolean;
  units?: MeasurementSystem;
  onAddGraphic?: (file: File, logo: MockupLogo) => void;
  onReplaceGraphic?: (file: File, logoId: string) => void;
};

export const useGraphicsPanel = ({
  disabled = false,
  onAddGraphic,
  onReplaceGraphic,
}: UseGraphicsPanelProps) => {
  const dispatch = useDispatch();
  const logos = useSelector((state: TRootState) => state.app.mockupLogos);
  const selectedId = useSelector((state: TRootState) => state.app.selectedGraphicId);
  const styleRegions = useSelector((state: TRootState) => state.app.styleRegions);

  const [isConversionLoading, setIsConversionLoading] = useState(false);
  const [conversionReplacingId, setConversionReplacingId] = useState<string | null>(null);

  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceTargetIdRef = useRef<string | null>(null);

  const selectedLogo = useMemo(
    () => logos.find(l => l.id === selectedId) ?? null,
    [logos, selectedId],
  );

  const regionOptions = useMemo(() => {
    if (styleRegions.length > 0) {
      return styleRegions.map((r, i) => ({
        id: i,
        value: r.id,
        label: convertRegionName(r.name) || r.id,
      }));
    }
    const seen = new Set<string>();
    return logos
      .map(l => l.region)
      .filter(r => r && !seen.has(r) && seen.add(r))
      .map((r, i) => ({ id: i, value: r, label: r }));
  }, [styleRegions, logos]);

  const buildLogoFromFile = useCallback(
    async (file: File): Promise<MockupLogo> => {
      const { imageSrc, imageSize } = await fileToImageUrl(file, CLOUD_CONVERT_API_KEY);
      const displayName = nameWithoutExtension(file.name);
      const id = crypto.randomUUID();

      const region = findBestAvailableRegionForNewLogo(styleRegions, logos);
      const size = region ? calculateLogoSizeForRegion(imageSize, region, false) : { x: 5, y: 5 };
      const transform = region
        ? getInitialTransformFromRegion(region, size, false)
        : { translation: { x: 0, y: 0 }, rotation: 0 };

      return {
        id,
        name: file.name,
        displayName,
        imageSrc,
        imageSize,
        region: region?.id ?? styleRegions[0]?.id ?? logos[0]?.region ?? '',
        size,
        technique: LogoTechnique.Rubber,
        transform,
      };
    },
    [styleRegions, logos],
  );

  const handleSelectLogo = useCallback(
    (id: string) => {
      dispatch(setSelectedGraphicId(id === selectedId ? null : id));
    },
    [dispatch, selectedId],
  );

  const handleDeselect = useCallback(() => {
    dispatch(setSelectedGraphicId(null));
  }, [dispatch]);

  const processAdd = useCallback(
    async (file: File) => {
      const showLoading = needsCloudConvert(file.name);
      if (showLoading) {
        setIsConversionLoading(true);
      }
      try {
        const logo = await buildLogoFromFile(file);
        dispatch(addGraphic(logo));
        onAddGraphic?.(file, logo);
      } finally {
        if (showLoading) {
          setIsConversionLoading(false);
        }
      }
    },
    [buildLogoFromFile, dispatch, onAddGraphic],
  );

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(file => processAdd(file));
    },
    [processAdd],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_LOGO_FILES,
    maxFiles: 1,
    multiple: false,
    onDropAccepted,
    noClick: true,
    useFsAccessApi: false,
    disabled,
  });

  const handleReplaceClick = useCallback((e: React.MouseEvent, logoId: string) => {
    e.stopPropagation();
    replaceTargetIdRef.current = logoId;
    replaceInputRef.current?.click();
  }, []);

  const processReplace = useCallback(
    async (file: File, logoId: string) => {
      const logo = logos.find(l => l.id === logoId);
      if (!logo) return;

      setConversionReplacingId(logoId);
      const showLoading = needsCloudConvert(file.name);
      if (showLoading) {
        setIsConversionLoading(true);
      }
      try {
        const { imageSrc, imageSize } = await fileToImageUrl(file, CLOUD_CONVERT_API_KEY);

        // Size: keep current width, scale height by new image aspect ratio. Transform stays unchanged.
        const nextSize = calculateLogoSizeRetainingWidth(imageSize, logo.size.x);

        dispatch(
          replaceGraphicImage({
            id: logoId,
            imageSrc,
            imageSize,
            size: nextSize,
          }),
        );
        onReplaceGraphic?.(file, logoId);
      } finally {
        if (showLoading) {
          setIsConversionLoading(false);
        }
        setConversionReplacingId(null);
      }
    },
    [dispatch, logos, onReplaceGraphic],
  );

  const handleReplaceFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const targetId = replaceTargetIdRef.current;
      if (file && targetId) processReplace(file, targetId);
      e.target.value = '';
      replaceTargetIdRef.current = null;
    },
    [processReplace],
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent, logo: MockupLogo) => {
      e.stopPropagation();
      dispatch(removeGraphic(logo.id));
    },
    [dispatch],
  );

  return {
    ACCEPTED_FILE_TYPES,
    conversionReplacingId,
    getRootProps,
    getInputProps,
    handleAddClick: open,
    isDragActive,
    handleDeleteClick,
    handleDeselect,
    handleReplaceClick,
    handleReplaceFileChange,
    handleSelectLogo,
    isConversionLoading,
    logos,
    regionOptions,
    replaceInputRef,
    selectedId,
    selectedLogo,
    styleRegions,
  };
};
